import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { FaDatabase, FaPlus } from "react-icons/fa";
import {
  SiPostgresql,
  SiMysql,
  SiSnowflake,
  SiShopify,
  SiMetabase,
  SiInstagram,
} from "react-icons/si";

type ConnectionTypeModalProps = {
  open: boolean;
  onClose: () => void;
};

type DatabaseType = {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  colorClass: string;
};

const databaseTypes: DatabaseType[] = [
  {
    id: "postgresql",
    name: "PostgreSQL",
    description: "Open-source relational database",
    icon: <SiPostgresql className="w-7 h-7" />,
    route: "/connections/new/postgresql",
    colorClass: "text-blue-600",
  },
  // {
  //   id: "mysql",
  //   name: "MySQL",
  //   description: "Popular open-source database",
  //   icon: <SiMysql className="w-7 h-7" />,
  //   route: "/connections/new/mysql",
  //   colorClass: "text-orange-600",
  // },
  // {
  //   id: "snowflake",
  //   name: "Snowflake",
  //   description: "Cloud data warehouse",
  //   icon: <SiSnowflake className="w-7 h-7" />,
  //   route: "/connections/new/snowflake",
  //   colorClass: "text-cyan-600",
  // },
  {
    id: "shopify",
    name: "Shopify",
    description: "E-commerce platform integration",
    icon: <SiShopify className="w-7 h-7" />,
    route: "/connections/new/shopify",
    colorClass: "text-green-600",
  },
  {
    id: "metabase",
    name: "Metabase",
    description: "Business intelligence & analytics",
    icon: <SiMetabase className="w-7 h-7" />,
    route: "/connections/new/metabase",
    colorClass: "text-purple-600",
  },
  {
    id: "instagram",
    name: "Instagram",
    description: "Social media platform integration",
    icon: <SiInstagram className="w-7 h-7" />,
    route: "/connections/new/instagram",
    colorClass: "text-pink-600",
  },
];

const ConnectionTypeModal = ({ open, onClose }: ConnectionTypeModalProps) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.showModal();
    } else if (!open && modalRef.current) {
      modalRef.current.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
    }
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  const handleSelectDatabase = (route: string) => {
    onClose();
    navigate(route);
  };

  const filteredDatabases = databaseTypes.filter(
    (db) =>
      db.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      db.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <dialog
      ref={modalRef}
      className="modal backdrop-blur-xs"
      onClick={handleBackdropClick}
    >
      <div className="modal-box max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold">Connectors</h3>
            <p className="text-sm text-base-content/60 mt-1">
              Choose a data source to connect to your workspace
            </p>
          </div>
          <form method="dialog">
            <button
              onClick={onClose}
              className="btn btn-sm btn-circle btn-ghost"
              aria-label="Close modal"
            >
              ✕
            </button>
          </form>
        </div>

        {/* Search Bar */}
        <div className="form-control mb-6">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input input-bordered input-sm w-full"
            autoFocus
          />
        </div>

        {/* Database Grid */}
        {filteredDatabases.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredDatabases.map((db) => (
              <div
                key={db.id}
                onClick={() => handleSelectDatabase(db.route)}
                className="card bg-base-100 border border-base-200 group hover:border-base-300 hover:bg-base-100/80 transition-all cursor-pointer"
              >
                <div className="card-body p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-base-200/50`}>
                        <div className={db.colorClass}>{db.icon}</div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{db.name}</h4>
                        <p className="text-xs text-base-content/60 mt-0.5">
                          {db.description}
                        </p>
                      </div>
                    </div>
                    <div className="w-7 h-7 flex items-center justify-center rounded-full transition-all group-hover:bg-base-300">
                      <FaPlus size={10} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-base-content/30 mb-4">
              <FaDatabase className="w-12 h-12" />
            </div>
            <p className="text-base-content/60 text-center">
              No connections found matching{" "}
              <span className="font-semibold">"{searchQuery}"</span>
            </p>
            <button
              onClick={() => setSearchQuery("")}
              className="btn btn-sm btn-ghost mt-4"
            >
              Clear search
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
};

export default ConnectionTypeModal;
